/**
 * Hook to fetch and cache public settings
 * Used by quote-link pages and PWA
 */

import { useState, useEffect } from "react";
import { getPublicSettings } from "../features/systemSettings/services/systemSettingsService.js";

let cachedSettings = null;
let cacheTimestamp = null;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Hook to get public settings
 * @returns {Object} { settings, loading, error }
 */
export const usePublicSettings = () => {
  const [settings, setSettings] = useState(cachedSettings || {});
  const [loading, setLoading] = useState(!cachedSettings);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check cache first
    if (cachedSettings && cacheTimestamp && Date.now() - cacheTimestamp < CACHE_TTL) {
      setSettings(cachedSettings);
      setLoading(false);
      return;
    }

    // Fetch from API
    const fetchSettings = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getPublicSettings();
        if (response.success) {
          cachedSettings = response.data || {};
          cacheTimestamp = Date.now();
          setSettings(cachedSettings);
        } else {
          throw new Error(response.error || "Failed to load settings");
        }
      } catch (err) {
        console.error("Failed to load public settings:", err);
        setError(err.message);
        // Fallback to empty object
        setSettings({});
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  return { settings, loading, error };
};

/**
 * Get a specific setting value with fallback
 * @param {string} key - Setting key
 * @param {*} defaultValue - Default value if not found
 * @returns {*} Setting value or defaultValue
 */
export const getPublicSetting = (settings, key, defaultValue = null) => {
  if (!settings || typeof settings !== "object") {
    return defaultValue;
  }
  return settings[key] !== undefined ? settings[key] : defaultValue;
};
